/**
 * ChampMontant — une somme d'argent, avec sa devise. La valeur est en
 * CENTIMES dans le code (un entier, jamais un flottant), le texte est celui
 * qu'un humain tape : « 49,90 », « 49.9 », « 1 234,5 ».
 *
 *   <ChampMontant etiquette="Prix" centimes={prix} onChange={setPrix} devise="EUR" />
 *   <ChampMontant etiquette="Prix" centimes={prix} onChange={setPrix}
 *     devise={devise} devises={["EUR", "USD"]} onDevise={setDevise} />
 *   <ChampMontant grand centimes={solde} onChange={setSolde} devise="USDT" aide="50 USDT minimum" />
 *
 * Vide, c'est `null` — pas zéro (une absence d'info n'est pas une valeur). Le
 * champ garde ce qu'on tape tant qu'on y est, et le remet en forme en sortant
 * (« 49,9 » → « 49,90 »). Deux décimales au plus ; un texte qui n'est pas un
 * montant passe le cadre au rouge sans toucher la valeur. La devise se change
 * au bout du champ (un menu sous le bouton), pas dans un `<select>` à côté.
 * Les chiffres sont en Almarena, tabulaires.
 *
 * Successeur des neuf `inputMode="decimal"` + `<select>` de devise écrits à la
 * main : `TransactionComposerModal`, `PricingTab`, `ScriptMessageEditor`,
 * `GameComposerModal`, `games/OffresView`, `regie-test/TransactionModale`,
 * et le montant des cartes d'Helios.
 */
import { useEffect, useRef, useState, type ReactNode, type Ref } from "react";

import { Button } from "../Button";
import { Champ, type TailleChamp } from "../Champ";
import { ChevronDown } from "../icons";
import { useMenu } from "../Menu";
import { cn } from "../utils";
import "./saisie.css";

/** Comment une devise s'écrit : son symbole, avant ou après le nombre. Une devise absente s'écrit par son code, après. */
export const SYMBOLES_DEVISE: Record<string, { symbole: string; avant: boolean }> = {
  EUR: { symbole: "€", avant: false },
  USD: { symbole: "$", avant: true },
  GBP: { symbole: "£", avant: true },
  CHF: { symbole: "CHF", avant: false },
  CAD: { symbole: "CA$", avant: true },
  USDT: { symbole: "USDT", avant: false },
  USDC: { symbole: "USDC", avant: false },
};

export function symboleDe(devise: string): { symbole: string; avant: boolean } {
  return SYMBOLES_DEVISE[devise.toUpperCase()] ?? { symbole: devise.toUpperCase(), avant: false };
}

/**
 * Le texte d'un humain → des centimes. `null` pour un texte vide, `undefined`
 * pour un texte qui n'est pas un montant. Accepte la virgule ou le point, les
 * espaces de milliers, un symbole collé ; refuse plus de deux décimales.
 *   « 49,90 » → 4990 · « 49.9 » → 4990 · « 1 234,5 » → 123450 · « 1,234.50 » → 123450 · « » → null · « 4,999 » → undefined
 */
export function centimesDepuisTexte(texte: string, options: { negatif?: boolean } = {}): number | null | undefined {
  let s = texte.replace(/[\s  ']/g, "").replace(/[€$£]|usdt|usdc|eur|usd|chf/gi, "");
  if (s === "") return null;
  let signe = 1;
  if (s.startsWith("-")) {
    if (!options.negatif) return undefined;
    signe = -1;
    s = s.slice(1);
  }
  // « 1,234.50 » ou « 1.234,50 » : le DERNIER séparateur est la décimale, les autres des milliers.
  const dernier = Math.max(s.lastIndexOf(","), s.lastIndexOf("."));
  let entier = s;
  let decimales = "";
  if (dernier >= 0) {
    const apres = s.slice(dernier + 1);
    const avant = s.slice(0, dernier);
    // Plus de deux chiffres après le dernier séparateur (« 4,999 », « 1.234 ») : ambigu, refusé plutôt que deviné.
    if (apres.length > 2) return undefined;
    decimales = apres;
    entier = avant.replace(/[.,]/g, "");
  }
  if (!/^\d*$/.test(entier) || !/^\d*$/.test(decimales) || entier + decimales === "") return undefined;
  const c = Number(entier || "0") * 100 + Number((decimales + "00").slice(0, 2));
  return Number.isSafeInteger(c) ? signe * c : undefined;
}

/** Des centimes → le texte du champ, sans symbole : 4990 → « 49,90 » (fr), « 49.90 » (en). */
export function texteDepuisCentimes(centimes: number, locale = "fr-FR"): string {
  return new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(centimes / 100);
}

export interface ChampMontantProps {
  /** La valeur, en centimes. `null` = vide. */
  centimes: number | null;
  onChange: (centimes: number | null) => void;
  /** Le code de la devise : EUR, USD, USDT… */
  devise: string;
  /** Les devises qu'on peut choisir ; avec `onDevise`, le symbole devient un bouton. */
  devises?: readonly string[];
  onDevise?: (devise: string) => void;
  etiquette?: ReactNode;
  aide?: ReactNode;
  /** L'erreur de l'écran (« 50 USDT minimum ») ; un texte illisible a la sienne, `illisibleLabel`. */
  erreur?: ReactNode;
  /** L'étiquette du bouton de devise pour un lecteur d'écran — déjà traduite (défaut « changer de devise »). */
  changerDeviseLabel?: string;
  /** Quand le texte tapé n'est pas un montant — déjà traduit (défaut « Ce n'est pas un montant. »). */
  illisibleLabel?: string;
  placeholder?: string;
  /** Le format des nombres (séparateurs). Défaut fr-FR. */
  locale?: string;
  /** Accepter une somme négative (un ajustement). */
  negatif?: boolean;
  /** La somme en grand (Almarena, 28 px) : le montant d'un écran de paiement. */
  grand?: boolean;
  taille?: TailleChamp;
  disabled?: boolean;
  autoFocus?: boolean;
  id?: string;
  /** Dans un `<form>` : un `<input hidden>` porte les centimes sous ce nom. */
  name?: string;
  "aria-label"?: string;
  ref?: Ref<HTMLInputElement>;
  className?: string;
}

export function ChampMontant({
  centimes,
  onChange,
  devise,
  devises,
  onDevise,
  etiquette,
  aide,
  erreur,
  changerDeviseLabel = "changer de devise",
  illisibleLabel = "Ce n'est pas un montant.",
  placeholder,
  locale = "fr-FR",
  negatif = false,
  grand = false,
  taille = "md",
  disabled,
  autoFocus,
  id,
  name,
  "aria-label": ariaLabel,
  ref,
  className,
}: ChampMontantProps) {
  const [texte, setTexte] = useState(() => (centimes === null ? "" : texteDepuisCentimes(centimes, locale)));
  const [illisible, setIllisible] = useState(false);
  const dedans = useRef(false);
  const { ouvrirSous, menu } = useMenu();

  // La valeur change ailleurs (un autre écran, un préréglage) : le champ la reprend, sauf pendant qu'on tape.
  useEffect(() => {
    if (dedans.current) return;
    setTexte(centimes === null ? "" : texteDepuisCentimes(centimes, locale));
    setIllisible(false);
  }, [centimes, locale]);

  const { symbole, avant } = symboleDe(devise);
  const choisissable = !!(devises && devises.length > 1 && onDevise);
  const signe = choisissable ? (
    <Button
      variant="ghost"
      size={grand ? "lg" : "sm"}
      className="si-dans-le-champ si-devise"
      iconRight={<ChevronDown weight="bold" aria-hidden className="h-[0.9em] w-[0.9em]" />}
      aria-label={`${devise}, ${changerDeviseLabel}`}
      aria-haspopup="menu"
      disabled={disabled}
      onClick={(e) =>
        ouvrirSous(
          e.currentTarget,
          devises!.map((d) => ({ label: d, sous: symboleDe(d).symbole !== d ? symboleDe(d).symbole : undefined, coche: d === devise, onClick: () => onDevise!(d) })),
          { alignement: "droite" },
        )
      }
    >
      {symbole}
    </Button>
  ) : (
    <span className="font-medium text-muted" aria-hidden>
      {symbole}
    </span>
  );

  return (
    <>
      <Champ
        ref={ref as Ref<HTMLInputElement | HTMLTextAreaElement>}
        id={id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        spellCheck={false}
        etiquette={etiquette}
        aide={aide}
        erreur={erreur ?? (illisible ? illisibleLabel : undefined)}
        aria-label={etiquette === undefined ? (ariaLabel ?? devise) : undefined}
        placeholder={placeholder ?? texteDepuisCentimes(0, locale)}
        autoFocus={autoFocus}
        disabled={disabled}
        taille={grand ? "lg" : taille}
        icone={avant && !choisissable ? signe : undefined}
        suffixe={!avant || choisissable ? signe : undefined}
        value={texte}
        className={cn("si-montant", grand && "si-montant-grand", className)}
        onFocus={() => {
          dedans.current = true;
        }}
        onChange={(e) => {
          const v = e.target.value;
          setTexte(v);
          const c = centimesDepuisTexte(v, { negatif });
          if (c === undefined) {
            setIllisible(true);
            // Un texte illisible ne vaut RIEN : la valeur passe à `null`, jamais l'ancien montant —
            // sinon un écran garde le dernier prix lisible et laisse partir une vente à un prix
            // que le chatteur n'a pas tapé (relecture adverse, 02-11-2026).
            if (centimes !== null) onChange(null);
            return;
          }
          setIllisible(false);
          if (c !== centimes) onChange(c);
        }}
        onBlur={() => {
          dedans.current = false;
          const c = centimesDepuisTexte(texte, { negatif });
          if (c !== undefined) setTexte(c === null ? "" : texteDepuisCentimes(c, locale));
        }}
      />
      {name !== undefined && <input type="hidden" name={name} value={centimes ?? ""} />}
      {menu}
    </>
  );
}
